import { DateTime } from 'luxon';
import { Box, Button, Select } from 'theme-ui';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

interface DateListNavigatorProps {
  dates: DateTime[];
  value: string | undefined;
  onChange: (date: DateTime) => void;
}

interface NavigationButtonProps {
  direction: 'previous' | 'next';
  onClick: () => void;
  disabled: boolean;
  color: string;
}

function NavigationButton({ direction, onClick, disabled, color }: NavigationButtonProps) {
  const Icon = direction === 'previous' ? FaChevronLeft : FaChevronRight;
  const ariaLabel = direction === 'previous' ? 'Previous date' : 'Next date';

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      sx={{
        position: 'absolute',
        [direction === 'previous' ? 'left' : 'right']: 0,
        top: 0,
        bottom: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: '1px solid',
        borderColor: color,
        bg: disabled ? '#dad6d0' : color,
        [direction === 'previous' ? 'borderRight' : 'borderLeft']: 'none',
        [direction === 'previous' ? 'borderTopRightRadius' : 'borderTopLeftRadius']: 0,
        [direction === 'previous' ? 'borderBottomRightRadius' : 'borderBottomLeftRadius']: 0,
        px: '6px',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <Icon />
    </Button>
  );
}

// TODO: Make stylable

export default function DateListNavigator({ dates, value, onChange }: DateListNavigatorProps) {
  const sortedDates = [...dates].sort((a, b) => a.toMillis() - b.toMillis());
  const currentDateIndex = sortedDates.findIndex(date => date.toISODate() === value);
  const currentDate = currentDateIndex >= 0 ? sortedDates[currentDateIndex] : sortedDates[0];

  const hasPrevious = currentDateIndex > 0;
  const hasNext = currentDateIndex < sortedDates.length - 1;

  const controlColor = 'secondary';

  const handlePrevious = () => {
    if (hasPrevious) {
      onChange(sortedDates[currentDateIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onChange(sortedDates[currentDateIndex + 1]);
    }
  };

  const handleSelectChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(DateTime.fromISO(evt.target.value));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: ['100%', 'auto'],
        maxWidth: [null, 360]
      }}
    >
      <Select
        className='date-list-navigator'
        value={currentDate?.toISODate() || ''}
        onChange={handleSelectChange}
        py={1}
        sx={{
          width: '100%',
          textAlign: 'center',
          textAlignLast: 'center',
          fontSize: 2,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: controlColor,
          paddingLeft: '38px',
          paddingRight: '38px',
          '& + svg': { display: 'none' }
        }}
      >
        {sortedDates.map(date => (
          <option key={date.toISODate()} value={date.toISODate()}>
            {date.toLocaleString({
              weekday: 'long',
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </option>
        ))}
      </Select>

      <NavigationButton
        direction="previous"
        onClick={handlePrevious}
        disabled={!hasPrevious}
        color={controlColor}
      />

      <NavigationButton
        direction="next"
        onClick={handleNext}
        disabled={!hasNext}
        color={controlColor}
      />
    </Box>
  );
}
